import { useState, type ReactNode } from 'react'
import { CodeNode } from '@lexical/code-core'
import { HorizontalRuleExtension } from '@lexical/extension'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin'
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { LexicalExtensionComposer } from '@lexical/react/LexicalExtensionComposer'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import { defineExtension, type EditorState } from 'lexical'
import { URL_MATCHER } from '@/features/contract/const/contract-lexical-editor.ts'
import { ContractLexicalSlashMenu } from './contract-lexical-slash-menu.tsx'
import { ContractLexicalToolbar, LinkDialog } from './contract-lexical-toolbar.tsx'
import { ContractVariableNode } from './contract-variable-node.ts'
import { ContractVariablePlugin } from './contract-variable-plugin.tsx'

export type ContractLexicalContent = Record<string, unknown>

const theme = {
  code: 'my-3 rounded-lg bg-slate-900 px-4 py-3 font-mono text-sm leading-6 text-slate-100',
  hr: 'my-4 h-2 cursor-pointer border-0 border-t border-border',
  hrSelected: 'outline-2 outline-accent',
  heading: {
    h1: 'mb-4 mt-6 text-2xl font-semibold leading-tight text-content first:mt-0',
    h2: 'mb-3 mt-5 text-xl font-semibold leading-tight text-content first:mt-0',
    h3: 'mb-2 mt-4 text-lg font-semibold leading-tight text-content first:mt-0'
  },
  link: 'text-accent underline decoration-accent/40 underline-offset-2',
  list: {
    listitem: 'my-1',
    listitemChecked:
      'relative ml-0 list-none pl-6 line-through before:absolute before:left-0 before:top-1.5 before:size-4 before:rounded before:border before:border-accent before:bg-accent before:content-["✓"] before:text-center before:text-xs before:leading-4 before:text-white',
    listitemUnchecked:
      'relative ml-0 list-none pl-6 before:absolute before:left-0 before:top-1.5 before:size-4 before:rounded before:border before:border-border before:content-[""]',
    nested: {
      listitem: 'list-none',
      list: 'ml-5'
    },
    ol: 'my-2 ml-6 list-decimal',
    ul: 'my-2 ml-6 list-disc'
  },
  paragraph: 'mb-3 min-h-7 leading-7 last:mb-0',
  quote: 'my-3 border-l-4 border-[var(--primary)] bg-[var(--accent-soft)] px-4 py-2 italic text-content-muted',
  table: 'my-4 w-full border-collapse overflow-hidden text-sm',
  tableCell: 'border border-border px-3 py-2 align-top',
  tableCellHeader: 'border border-border bg-surface-muted px-3 py-2 font-medium align-top',
  tableScrollableWrapper: 'max-w-full overflow-x-auto',
  text: {
    bold: 'font-semibold',
    code: 'rounded bg-surface-muted px-1 py-0.5 font-mono text-[0.9em]',
    highlight: 'rounded bg-yellow-200 px-0.5',
    italic: 'italic',
    strikethrough: 'line-through',
    subscript: 'align-sub text-[0.75em]',
    superscript: 'align-super text-[0.75em]',
    underline: 'underline underline-offset-2'
  }
}

function EditorOnChange({ onChange }: { onChange: (content: ContractLexicalContent) => void }) {
  return (
    <OnChangePlugin
      ignoreSelectionChange
      onChange={(editorState: EditorState) => {
        onChange(editorState.toJSON() as unknown as ContractLexicalContent)
      }}
    />
  )
}

function EditorPlugins({
  onChange,
  placeholder,
  readOnly,
  enableVariables
}: {
  onChange?: (content: ContractLexicalContent) => void
  placeholder: ReactNode
  readOnly: boolean
  enableVariables: boolean
}) {
  const [editor] = useLexicalComposerContext()
  const [linkOpen, setLinkOpen] = useState(false)

  return (
    <>
      {!readOnly ? <ContractLexicalToolbar onRequestLink={() => setLinkOpen(true)} /> : null}
      <div className="bg-surface-muted min-h-0 min-w-0 flex-1 overflow-auto overscroll-contain p-4 print:overflow-visible print:p-0">
        {/* A4 210 × 297mm，四边 20mm；窄屏横向滚动以保持与打印相同的换行宽度。 */}
        <div className="relative mx-auto box-border min-h-[70.1575rem] w-[49.6063rem] bg-white px-[4.7244rem] py-[4.7244rem] text-black shadow-sm print:min-h-0 print:w-auto print:p-0 print:shadow-none">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                aria-label="合同正文"
                className="contract-editor-content min-h-[60.7087rem] text-sm leading-7 outline-none print:min-h-0 print:p-0"
              />
            }
            ErrorBoundary={LexicalErrorBoundary}
            placeholder={
              <div className="text-content-soft pointer-events-none absolute top-[4.7244rem] left-[4.7244rem] text-sm leading-7">
                {placeholder}
              </div>
            }
          />
        </div>
        <HistoryPlugin />
        {enableVariables ? <ContractVariablePlugin readOnly={readOnly} /> : null}
        <ListPlugin hasStrictIndent />
        <CheckListPlugin />
        <LinkPlugin attributes={{ rel: 'noopener noreferrer', target: '_blank' }} />
        <AutoLinkPlugin matchers={[URL_MATCHER]} />
        <TablePlugin hasCellBackgroundColor hasCellMerge hasHorizontalScroll hasTabHandler />
        {!readOnly ? <ContractLexicalSlashMenu onRequestLink={() => setLinkOpen(true)} /> : null}
        {onChange ? <EditorOnChange onChange={onChange} /> : null}
      </div>
      {!readOnly ? <LinkDialog editor={editor} onClose={() => setLinkOpen(false)} open={linkOpen} /> : null}
    </>
  )
}

export function ContractLexicalEditor({
  initialValue,
  onChange,
  readOnly = false,
  enableVariables = false,
  placeholder = '开始编写合同正文…'
}: {
  initialValue: ContractLexicalContent
  onChange?: (content: ContractLexicalContent) => void
  readOnly?: boolean
  enableVariables?: boolean
  placeholder?: ReactNode
}) {
  const [extension] = useState(() =>
    defineExtension({
      name: 'contract-template',
      dependencies: [HorizontalRuleExtension],
      editable: !readOnly,
      $initialEditorState: JSON.stringify(initialValue),
      namespace: 'contract-template',
      nodes: [
        AutoLinkNode,
        CodeNode,
        ContractVariableNode,
        HeadingNode,
        LinkNode,
        ListItemNode,
        ListNode,
        QuoteNode,
        TableCellNode,
        TableNode,
        TableRowNode
      ],
      onError: (error: Error) => {
        throw error
      },
      theme
    })
  )

  return (
    <LexicalExtensionComposer extension={extension} contentEditable={null}>
      <div className="bg-surface flex h-full min-h-0 min-w-0 flex-col overflow-hidden print:h-auto print:overflow-visible">
        <EditorPlugins
          enableVariables={enableVariables}
          onChange={onChange}
          placeholder={placeholder}
          readOnly={readOnly}
        />
      </div>
    </LexicalExtensionComposer>
  )
}
